import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { 
  FiUsers, FiFileText, FiTrendingUp, FiThumbsUp, FiThumbsDown, 
  FiClock, FiBarChart2, FiActivity, FiArrowLeft, FiRefreshCw,
  FiDollarSign, FiImage, FiLayers, FiTarget, FiCheckSquare, FiSquare,
  FiPlus, FiTrash2, FiEdit2, FiX, FiCheck, FiAlertCircle, FiMessageSquare
} from 'react-icons/fi';

function FounderDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics', 'roadmap', or 'issues'
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Task tracker state
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'P2', category: 'Features', effort: 'Medium' });
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'done'

  // Issues state
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issueStatusCounts, setIssueStatusCounts] = useState({});
  const [issueFilter, setIssueFilter] = useState('open');

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await api.getFounderStats();
      setStats(data);
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      // If 403, redirect to dashboard silently
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setTimeout(() => navigate('/dashboard'), 100);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      setTasksLoading(true);
      const data = await api.getFounderTasks();
      setTasks(data.tasks || []);
    } catch (err) {
      // Silently fail if access denied (already handled by fetchStats)
      if (err.response?.status !== 403) {
        console.error('Failed to load tasks:', err);
      }
    } finally {
      setTasksLoading(false);
    }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      const data = await api.createFounderTask(newTask);
      setTasks([...tasks, data.task]);
      setNewTask({ title: '', description: '', priority: 'P2', category: 'Features', effort: 'Medium' });
      setShowAddTask(false);
    } catch (err) {
      console.error('Failed to add task:', err);
    }
  };

  const toggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';
    try {
      await api.updateFounderTask(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.deleteFounderTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const fetchIssues = async (status = null) => {
    try {
      setIssuesLoading(true);
      const data = await api.getIssues(status);
      setIssues(data.issues || []);
      setIssueStatusCounts(data.status_counts || {});
    } catch (err) {
      console.error('Failed to load issues:', err);
    } finally {
      setIssuesLoading(false);
    }
  };

  const updateIssueStatus = async (issueId, newStatus) => {
    try {
      await api.updateIssue(issueId, { status: newStatus });
      setIssues(issues.map(i => i.id === issueId ? { ...i, status: newStatus } : i));
      // Update counts
      setIssueStatusCounts(prev => ({
        ...prev,
        [issues.find(i => i.id === issueId)?.status]: (prev[issues.find(i => i.id === issueId)?.status] || 1) - 1,
        [newStatus]: (prev[newStatus] || 0) + 1
      }));
    } catch (err) {
      console.error('Failed to update issue:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTasks();
    fetchIssues(issueFilter === 'all' ? null : issueFilter);
  }, []);

  useEffect(() => {
    if (activeTab === 'issues') {
      fetchIssues(issueFilter === 'all' ? null : issueFilter);
    }
  }, [issueFilter, activeTab]);

  // If access denied, show nothing (redirect is happening)
  if (accessDenied) {
    return null;
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <FiRefreshCw className="animate-spin text-primary mx-auto mb-4" size={32} />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={fetchStats} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color = "text-primary" }) => (
    <div className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <Icon className={`${color}`} size={24} />
        {subValue && <span className="text-xs text-muted-foreground">{subValue}</span>}
      </div>
      <div className="text-3xl font-bold text-foreground mb-1">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );

  const MiniChart = ({ data, label, color = "bg-primary" }) => {
    const max = Math.max(...data.map(d => d.count), 1);
    return (
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-sm font-medium text-foreground mb-4">{label}</h3>
        <div className="flex items-end gap-1 h-24">
          {data.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div 
                className={`w-full ${color} rounded-t opacity-80 hover:opacity-100 transition-opacity`}
                style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? '4px' : '0' }}
                title={`${d.date}: ${d.count}`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
          <span>{data[0]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  // Filter tasks based on filter state
  const filteredTasks = tasks.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'pending') return t.status !== 'done';
    if (filter === 'done') return t.status === 'done';
    return true;
  });

  // Group tasks by priority
  const groupedTasks = {
    P0: filteredTasks.filter(t => t.priority === 'P0'),
    P1: filteredTasks.filter(t => t.priority === 'P1'),
    P2: filteredTasks.filter(t => t.priority === 'P2'),
    P3: filteredTasks.filter(t => t.priority === 'P3'),
  };

  const priorityColors = {
    P0: 'bg-red-500/20 text-red-400 border-red-500/30',
    P1: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    P2: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    P3: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  const priorityLabels = {
    P0: '🔴 P0 - Critical',
    P1: '🟠 P1 - High Priority',
    P2: '🔵 P2 - Medium Priority',
    P3: '⚪ P3 - Low Priority / Backlog',
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <FiArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-foreground">Founder Dashboard</h1>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'analytics' ? 'Real-time app analytics' : activeTab === 'roadmap' ? 'Product roadmap & tasks' : 'User-reported issues'}
                  {lastUpdated && activeTab === 'analytics' && (
                    <span className="ml-2">• Updated {lastUpdated.toLocaleTimeString()}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Tab Switcher */}
              <div className="flex bg-secondary rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'analytics' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiBarChart2 className="inline mr-2" size={16} />
                  Analytics
                </button>
                <button
                  onClick={() => setActiveTab('roadmap')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'roadmap' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiCheckSquare className="inline mr-2" size={16} />
                  Roadmap
                </button>
                <button
                  onClick={() => setActiveTab('issues')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
                    activeTab === 'issues' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiAlertCircle className="inline mr-2" size={16} />
                  Issues
                  {issueStatusCounts.open > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {issueStatusCounts.open}
                    </span>
                  )}
                </button>
              </div>
              {activeTab === 'analytics' && (
                <button 
                  onClick={fetchStats}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} size={16} />
              Refresh
            </button>
              )}
          </div>
        </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* ISSUES TAB */}
        {activeTab === 'issues' && (
          <div>
            {/* Issues Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-bold text-foreground">User Issues</h2>
                <div className="flex gap-2">
                  {['all', 'open', 'in_progress', 'resolved'].map(f => (
                    <button
                      key={f}
                      onClick={() => setIssueFilter(f)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        issueFilter === f 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-secondary text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {f === 'all' ? 'All' : f === 'open' ? `Open (${issueStatusCounts.open || 0})` : f === 'in_progress' ? 'In Progress' : 'Resolved'}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => fetchIssues(issueFilter === 'all' ? null : issueFilter)}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors"
              >
                <FiRefreshCw className={issuesLoading ? 'animate-spin' : ''} size={16} />
                Refresh
              </button>
            </div>

            {/* Issues List */}
            {issuesLoading ? (
              <div className="text-center py-12">
                <FiRefreshCw className="animate-spin text-primary mx-auto mb-4" size={32} />
                <p className="text-muted-foreground">Loading issues...</p>
              </div>
            ) : issues.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FiMessageSquare className="mx-auto mb-4 opacity-50" size={48} />
                <p>No issues reported yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {issues.map(issue => (
                  <div key={issue.id} className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            issue.type === 'bug' ? 'bg-red-500/20 text-red-400' :
                            issue.type === 'feature' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {issue.type === 'bug' ? '🐛 Bug' : issue.type === 'feature' ? '💡 Feature' : '❓ Other'}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            issue.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                            issue.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                            issue.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {issue.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(issue.created_at).toLocaleDateString()} {new Date(issue.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-foreground mb-2">{issue.message}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>From: <span className="text-foreground">{issue.user_email}</span></span>
                          <span>Page: <span className="text-foreground">{issue.page}</span></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={issue.status}
                          onChange={(e) => updateIssueStatus(issue.id, e.target.value)}
                          className="px-3 py-1.5 bg-secondary border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stats Summary */}
            <div className="mt-8 grid grid-cols-4 gap-4">
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-foreground">{(issueStatusCounts.open || 0) + (issueStatusCounts.in_progress || 0) + (issueStatusCounts.resolved || 0) + (issueStatusCounts.closed || 0)}</div>
                <div className="text-sm text-muted-foreground">Total Issues</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-yellow-400">{issueStatusCounts.open || 0}</div>
                <div className="text-sm text-muted-foreground">Open</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-blue-400">{issueStatusCounts.in_progress || 0}</div>
                <div className="text-sm text-muted-foreground">In Progress</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-emerald-400">{issueStatusCounts.resolved || 0}</div>
                <div className="text-sm text-muted-foreground">Resolved</div>
              </div>
            </div>
          </div>
        )}

        {/* ROADMAP TAB */}
        {activeTab === 'roadmap' && (
          <div>
            {/* Roadmap Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-bold text-foreground">Product Roadmap</h2>
                <div className="flex gap-2">
                  {['all', 'pending', 'done'].map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        filter === f 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-secondary text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {f === 'all' ? 'All' : f === 'pending' ? 'To Do' : 'Done'}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setShowAddTask(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <FiPlus size={16} />
                Add Task
              </button>
            </div>

            {/* Add Task Form */}
            {showAddTask && (
              <div className="bg-card border border-primary/30 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">Add New Task</h3>
                  <button onClick={() => setShowAddTask(false)} className="text-muted-foreground hover:text-foreground">
                    <FiX size={20} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Task title"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <select
                    value={newTask.priority}
                    onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="P0">P0 - Critical</option>
                    <option value="P1">P1 - High</option>
                    <option value="P2">P2 - Medium</option>
                    <option value="P3">P3 - Low</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <select
                    value={newTask.category}
                    onChange={e => setNewTask({ ...newTask, category: e.target.value })}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Features">Features</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Analytics & Support">Analytics & Support</option>
                    <option value="Bug Fix">Bug Fix</option>
                    <option value="UX">UX</option>
                  </select>
                  <select
                    value={newTask.effort}
                    onChange={e => setNewTask({ ...newTask, effort: e.target.value })}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Low">Low Effort</option>
                    <option value="Medium">Medium Effort</option>
                    <option value="High">High Effort</option>
                  </select>
                </div>
                <textarea
                  placeholder="Description (optional)"
                  value={newTask.description}
                  onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                  rows={2}
                />
                <button
                  onClick={addTask}
                  disabled={!newTask.title.trim()}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  Add Task
                </button>
              </div>
            )}

            {/* Task Lists by Priority */}
            {tasksLoading ? (
              <div className="text-center py-12">
                <FiRefreshCw className="animate-spin text-primary mx-auto mb-4" size={32} />
                <p className="text-muted-foreground">Loading tasks...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedTasks).map(([priority, priorityTasks]) => (
                  priorityTasks.length > 0 && (
                    <div key={priority} className="bg-card rounded-xl border border-border overflow-hidden">
                      <div className={`px-6 py-3 border-b border-border ${priorityColors[priority]}`}>
                        <h3 className="font-semibold">{priorityLabels[priority]}</h3>
                        <p className="text-xs opacity-70">{priorityTasks.filter(t => t.status !== 'done').length} pending</p>
                      </div>
                      <div className="divide-y divide-border">
                        {priorityTasks.map(task => (
                          <div 
                            key={task.id} 
                            className={`px-6 py-4 flex items-center gap-4 hover:bg-secondary/30 transition-colors ${
                              task.status === 'done' ? 'opacity-60' : ''
                            }`}
                          >
                            <button
                              onClick={() => toggleTaskStatus(task.id, task.status)}
                              className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                                task.status === 'done' 
                                  ? 'bg-emerald-500 border-emerald-500 text-white' 
                                  : 'border-muted-foreground hover:border-primary'
                              }`}
                            >
                              {task.status === 'done' && <FiCheck size={14} />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium text-foreground ${task.status === 'done' ? 'line-through' : ''}`}>
                                {task.title}
                              </div>
                              {task.description && (
                                <div className="text-sm text-muted-foreground truncate">{task.description}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="px-2 py-1 text-xs rounded bg-secondary text-muted-foreground">
                                {task.category}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${
                                task.effort === 'Low' ? 'bg-green-500/20 text-green-400' :
                                task.effort === 'High' ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {task.effort}
                              </span>
                              <button
                                onClick={() => deleteTask(task.id)}
                                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
                {filteredTasks.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    {filter === 'done' ? 'No completed tasks yet' : 'No tasks found'}
                  </div>
                )}
              </div>
            )}

            {/* Summary Stats */}
            <div className="mt-8 grid grid-cols-4 gap-4">
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-foreground">{tasks.length}</div>
                <div className="text-sm text-muted-foreground">Total Tasks</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-emerald-400">{tasks.filter(t => t.status === 'done').length}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-orange-400">{tasks.filter(t => t.status !== 'done').length}</div>
                <div className="text-sm text-muted-foreground">Remaining</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-red-400">{tasks.filter(t => t.priority === 'P0' && t.status !== 'done').length}</div>
                <div className="text-sm text-muted-foreground">Critical</div>
              </div>
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <>
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={FiUsers} 
            label="Total Users" 
            value={stats?.users?.total || 0}
            subValue={`+${stats?.users?.this_week || 0} this week`}
            color="text-blue-400"
          />
          <StatCard 
            icon={FiActivity} 
            label="Active Users" 
            value={stats?.users?.active_this_week || 0}
            subValue="Last 7 days"
            color="text-green-400"
          />
          <StatCard 
            icon={FiFileText} 
            label="Content Generated" 
            value={stats?.content?.total || 0}
            subValue={`+${stats?.content?.this_week || 0} this week`}
            color="text-purple-400"
          />
          <StatCard 
            icon={FiThumbsUp} 
            label="Approval Rate" 
            value={`${stats?.satisfaction?.approval_rate || 0}%`}
            subValue="Satisfaction proxy"
            color="text-emerald-400"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {stats?.users?.daily_signups && (
            <MiniChart 
              data={stats.users.daily_signups} 
              label="Daily Signups (14 days)" 
              color="bg-blue-500"
            />
          )}
          {stats?.content?.daily_content && (
            <MiniChart 
              data={stats.content.daily_content} 
              label="Daily Content (14 days)" 
              color="bg-purple-500"
            />
          )}
        </div>

        {/* Detailed Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* User Breakdown */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <FiUsers className="text-blue-400" /> User Metrics
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Today</span>
                <span className="font-medium text-foreground">{stats?.users?.today || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">This Week</span>
                <span className="font-medium text-foreground">{stats?.users?.this_week || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">This Month</span>
                <span className="font-medium text-foreground">{stats?.users?.this_month || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active (7d)</span>
                <span className="font-medium text-green-400">{stats?.users?.active_this_week || 0}</span>
              </div>
            </div>
          </div>

          {/* Plan Distribution */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <FiDollarSign className="text-emerald-400" /> Plan Distribution
            </h3>
            <div className="space-y-3">
              {stats?.users?.plan_distribution && Object.entries(stats.users.plan_distribution).map(([plan, count]) => (
                <div key={plan} className="flex justify-between items-center">
                  <span className="text-muted-foreground capitalize">{plan}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-secondary rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${plan === 'free' ? 'bg-gray-400' : 'bg-emerald-400'}`}
                        style={{ width: `${(count / (stats?.users?.total || 1)) * 100}%` }}
                      />
                    </div>
                    <span className="font-medium text-foreground w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Satisfaction */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <FiThumbsUp className="text-purple-400" /> Content Satisfaction
            </h3>
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="text-5xl font-bold text-foreground mb-2">
                  {stats?.satisfaction?.approval_rate || 0}%
                </div>
                <div className="text-sm text-muted-foreground">Approval Rate</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-500/10 rounded-lg p-3">
                  <div className="text-lg font-bold text-emerald-400">{stats?.satisfaction?.approved || 0}</div>
                  <div className="text-xs text-muted-foreground">Approved</div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3">
                  <div className="text-lg font-bold text-red-400">{stats?.satisfaction?.rejected || 0}</div>
                  <div className="text-xs text-muted-foreground">Rejected</div>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-3">
                  <div className="text-lg font-bold text-amber-400">{stats?.satisfaction?.pending || 0}</div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement & Usage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Engagement */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <FiTarget className="text-cyan-400" /> Engagement
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-secondary/50 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{stats?.engagement?.total_clients || 0}</div>
                <div className="text-xs text-muted-foreground">Clients</div>
              </div>
              <div className="text-center p-4 bg-secondary/50 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{stats?.engagement?.total_campaigns || 0}</div>
                <div className="text-xs text-muted-foreground">Campaigns</div>
              </div>
              <div className="text-center p-4 bg-secondary/50 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{stats?.engagement?.avg_content_per_user || 0}</div>
                <div className="text-xs text-muted-foreground">Avg/User</div>
              </div>
            </div>
          </div>

          {/* Platform Distribution */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <FiBarChart2 className="text-pink-400" /> Platform Distribution
            </h3>
            <div className="space-y-3">
              {stats?.content?.platform_distribution && Object.entries(stats.content.platform_distribution).map(([platform, count]) => (
                <div key={platform} className="flex justify-between items-center">
                  <span className="text-muted-foreground capitalize">{platform}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-secondary rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-pink-400"
                        style={{ width: `${(count / (stats?.content?.total || 1)) * 100}%` }}
                      />
                    </div>
                    <span className="font-medium text-foreground w-12 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <FiTrendingUp className="text-amber-400" /> Top Users by Content Generated
          </h3>
          {stats?.top_users?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">#</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Content</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_users.map((user, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="py-3 px-4 text-foreground">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-foreground">{user.name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.plan === 'free' ? 'bg-gray-500/20 text-gray-400' :
                          user.plan === 'scale' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {user.plan}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-foreground">{user.content_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No user data yet</p>
          )}
        </div>

        {/* Usage Summary */}
        <div className="mt-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20 p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <FiLayers className="text-primary" /> Total Platform Usage
          </h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{stats?.usage?.total_ideas_generated || 0}</div>
              <div className="text-sm text-muted-foreground">Ideas Generated</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{stats?.usage?.total_images_generated || 0}</div>
              <div className="text-sm text-muted-foreground">Images Generated</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{stats?.usage?.total_credits_used || 0}</div>
              <div className="text-sm text-muted-foreground">Credits Used</div>
            </div>
          </div>
        </div>
          </>
        )}
      </main>
    </div>
  );
}

export default FounderDashboard;
